import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TaskListTemplateSectionModel, TaskListTemplateSectionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['taskListTemplateSections']

type TaskListTemplateSectionFilters = {
  string?: string,
  task_list_template_id?: number,
}

type UseTaskListTemplateSectionsOptions = {
  filters?: TaskListTemplateSectionFilters,
  performHttpRequests?: boolean,
  requestOptions?: TaskListTemplateSectionRequestOptions,
}

function useTaskListTemplateSections(options: UseTaskListTemplateSectionsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    string: filterString,
    task_list_template_id: filterTaskListTemplateId,
  } = filters

  const {
    updatedEntities: { taskListTemplateSections: taskListTemplateSectionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { taskListTemplateSections } = useSelector(reduxState => reduxState.entities)

  const filteredTaskListTemplateSections = useMemo(() => {
    const filtered = Object.values(taskListTemplateSections)
      .filter((taskListTemplateSection: TaskListTemplateSectionModel) => {
        const { id, task_list_template_id, title } = taskListTemplateSection

        const matchId = id.toString() === filterString
        const matchTitle = matchFilterString(title, filterString)
        const matchString = matchId || matchTitle

        const matchTaskListTemplateId = matchFilterNumber(task_list_template_id, filterTaskListTemplateId)

        return matchString && matchTaskListTemplateId
      })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [taskListTemplateSectionsUpdatedAt, JSON.stringify(filters)])

  const filteredTaskListTemplateSectionsCount = filteredTaskListTemplateSections.length
  const hasFilteredTaskListTemplateSections = !!filteredTaskListTemplateSectionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTaskListTemplateSectionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTaskListTemplateSections } = useReduxAction(
    // @ts-expect-error TS2345
    'taskListTemplateSections',
    'loadTaskListTemplateSections',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskListTemplateSections,
    filteredTaskListTemplateSectionsCount,
    hasFilteredTaskListTemplateSections,
    loading: loadingTaskListTemplateSections,
  }
}

export default useTaskListTemplateSections
